import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export type TabStripItem<T extends string> = { value: T; label: string; count?: number | null };

// A page's underline tabs (Usage, People). On a narrow screen the strip
// scrolls, and a fade on the right edge shows there is more.
export function TabStrip<T extends string>({
  tabs,
  active,
  onSelect,
  label,
}: {
  tabs: TabStripItem<T>[];
  active: T;
  onSelect: (t: T) => void;
  label: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const [more, setMore] = useState(false);
  // Counts arrive after the strip renders and widen it.
  const widths = tabs.map((t) => `${t.label}${t.count ?? ""}`).join();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    check();
    el.addEventListener("scroll", check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", check);
      ro.disconnect();
    };
  }, [widths]);
  return (
    <div className="relative">
      <nav
        ref={ref}
        className="flex gap-1 overflow-x-auto whitespace-nowrap border-b [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label={label}
      >
        {tabs.map((t) => (
          <button
            key={t.value}
            type="button"
            aria-current={t.value === active ? "page" : undefined}
            onClick={() => onSelect(t.value)}
            className={cn(
              "-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors",
              t.value === active
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
            {t.count != null && (
              <span className="tabular-nums text-muted-foreground">{t.count}</span>
            )}
          </button>
        ))}
      </nav>
      {more && (
        <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background to-transparent" />
      )}
    </div>
  );
}
